const UNREAL_SPEECH_API_KEY = 'zelelsVo3xp18WJvvomEhnTZ8efpZwdHjJAhKOK2OWOQ2nW4kr9zsA';
const UNREAL_SPEECH_API_URL = 'https://api.v8.unrealspeech.com';

export const generateSpeechStream = async (text: string): Promise<HTMLAudioElement> => {
  if (!text?.trim()) throw new Error("Text cannot be empty");

  const textToProcess = text
    .replace(/[\#\*\_`~]/g, '')
    .substring(0, 1000);

  const response = await fetch(`${UNREAL_SPEECH_API_URL}/stream`, {
    method: "POST",
    headers: {
      "Authorization": `Bearer ${UNREAL_SPEECH_API_KEY}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      Text: textToProcess,
      VoiceId: "Sierra",
      Bitrate: "48k",
      Speed: 0,
      Pitch: 1.0,
    }),
  });

  if (!response.ok || !response.body) {
    throw new Error(`TTS request failed: ${response.status} ${response.statusText}`);
  }

  // Create blob URL from response
  const blob = await response.blob();
  const audio = new Audio(URL.createObjectURL(blob));

  // Ensure audio is fully loaded before returning
  return new Promise((resolve, reject) => {
    audio.onloadeddata = () => resolve(audio);
    audio.onerror = () => reject(new Error('Failed to load audio'));
  });
};

interface TTSConfig {
  text: string;
  voiceId: string;
  speed: number;
  pitch: number;
  endpoint: string;
  timestampType?: string;
}

export const generateTextToSpeech = async (config: TTSConfig): Promise<{ success: boolean; audioContent?: string; OutputUri?: string; TimestampsUri?: string; error?: string }> => {
  try {
    if (!config.text?.trim()) {
      throw new Error("Text cannot be empty");
    }

    const endpoint = config.endpoint === 'synthesisTasks' ? '/synthesisTasks' : '/stream';
    
    const response = await fetch(`${UNREAL_SPEECH_API_URL}${endpoint}`, {
      method: "POST",
      headers: {
        "Authorization": `Bearer ${UNREAL_SPEECH_API_KEY}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        Text: config.text.replace(/[\#\*\_`~]/g, '').substring(0, 1000),
        VoiceId: config.voiceId,
        Bitrate: "64k",
        Speed: config.speed,
        Pitch: config.pitch,
        TimestampType: config.timestampType || 'word',
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`TTS request failed: ${response.status} ${errorText}`);
    }

    // For stream endpoint, return blob as base64
    if (endpoint === '/stream') {
      const blob = await response.blob();
      const arrayBuffer = await blob.arrayBuffer();
      const bytes = new Uint8Array(arrayBuffer);
      const chunkSize = 0x8000; // 32KB chunks to avoid call stack overflow
      let binary = '';
      for (let i = 0; i < bytes.length; i += chunkSize) {
        const chunk = bytes.subarray(i, i + chunkSize);
        // Using apply on small chunks prevents "Maximum call stack size exceeded"
        binary += String.fromCharCode.apply(null, Array.from(chunk));
      }
      const base64 = btoa(binary);

      return {
        success: true,
        audioContent: base64
      };
    }

    // For synthesisTasks endpoint, return JSON response
    const data = await response.json();
    return {
      success: true,
      OutputUri: data.OutputUri,
      TimestampsUri: data.TimestampsUri
    };

  } catch (error: any) {
    console.error('UnrealSpeech TTS error:', error);
    return {
      success: false,
      error: error.message || 'Failed to generate speech'
    };
  }
};

export interface SpeechSpeechConfig {
  text: string;
  voiceId?: string;
  bitrate?: string;
  speed?: number;
  pitch?: number;
}

export interface SpeechSpeechResult {
  audioUrl: string;
  timestampsUrl: string;
}

export const generateSpeechWithTimestamps = async (config: SpeechSpeechConfig): Promise<SpeechSpeechResult> => {
  if (!config.text?.trim()) throw new Error("Text cannot be empty");

  // Clean the text to remove markdown headers, bold symbols, etc. first, then take the first 1000 characters
  const cleanedText = config.text
    .replace(/[\#\*\_`~]/g, '')
    .substring(0, 1000);

  const response = await fetch(`${UNREAL_SPEECH_API_URL}/speech`, {
    method: "POST",
    headers: {
      "Authorization": `Bearer ${UNREAL_SPEECH_API_KEY}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      Text: cleanedText,
      VoiceId: config.voiceId || "Ethan",
      Bitrate: config.bitrate || "64k",
      Speed: config.speed || 0,
      Pitch: config.pitch || 1.0,
      TimestampType: "word",
    }),
  });

  if (!response.ok) {
    const errText = await response.text();
    throw new Error(`TTS speech request failed: ${response.status} ${errText}`);
  }

  const data = await response.json();
  return {
    audioUrl: data.OutputUri,
    timestampsUrl: data.TimestampsUri,
  };
};
